import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "@/api/client";
import type { TopicSummary } from "../../../types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/** 'YYYY-MM-DD HH:MM:SS' (UTC) → "Jul 6, 2026". */
function fmtSqlDate(sql: string): string {
  return new Date(sql.replace(" ", "T") + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * On-demand LLM digest of a topic's answers. The server gates on a stored
 * digest: if nothing new ran since the last summary, the click is free. Pins to
 * the topic FK (rename-proof) rather than the category string.
 */
export function TopicSummaryCard({ topicId, hasRuns, latestComparisonId }: { topicId: number; hasRuns: boolean; latestComparisonId: number | null }) {
  const [latest, setLatest] = useState<TopicSummary | null>(null);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.topicSummaries.list({ topic_id: topicId })
      .then((rows) => { if (!cancelled) setLatest(rows[0] ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [topicId]);

  const upToDate = latest?.data != null && latest.data.latest_comparison_id === latestComparisonId;

  const generate = async () => {
    setGenerating(true);
    setMessage("");
    try {
      const result = await api.topicSummaries.generate({ topic_id: topicId });
      setLatest(result);
      if (result.cached) setMessage("Already up to date — nothing new to summarize.");
    } catch (e: any) {
      setMessage(e?.message || "Could not generate the summary.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="bg-brand-50/40">
      <CardContent className="space-y-2.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">What the models are saying</span>
          <Button variant="secondary" size="sm" onClick={generate} disabled={generating || !hasRuns || upToDate}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            {generating ? "Summarizing…" : upToDate ? "Up to date" : latest ? "Refresh summary (1 AI call)" : "Summarize this topic (1 AI call)"}
          </Button>
        </div>
        {latest ? (
          <>
            <p className="text-[13.5px] leading-relaxed text-slate-700">{latest.summary}</p>
            <p className="text-[11.5px] text-slate-400">Generated {fmtSqlDate(latest.created_at)} from the answers below.</p>
          </>
        ) : (
          <p className="text-[12.5px] text-slate-500">
            {hasRuns
              ? "Get an AI-written digest of what the models consistently say in this topic — grounded in the stored answers, generated once per new data."
              : "Once this topic has completed runs, you can generate an AI-written digest of the answers."}
          </p>
        )}
        {message && <p className="text-[12px] text-slate-500">{message}</p>}
      </CardContent>
    </Card>
  );
}
